// YNX — BILLING MODULE (upgrade plan + paid add-ons via Stripe Checkout)
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Sparkles, Check, CreditCard, Crown, Plus } from 'lucide-react';
import { listPackages, createCheckout, getCheckoutStatus } from '../../lib/billing';

// Constants outside the component to keep useCallback dependency arrays stable.
const POLL_MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS  = 2000;

const SOLO_FEATURES = [
  '1 utilisateur',
  '50 factures / mois',
  '200 clients max.',
  'Tous les modules métier',
  'Assistant IA (limité)',
  'Studio Visuel IA (limité)',
  'Support communautaire',
];

const BUSINESS_FEATURES = [
  '✅ Utilisateurs illimités',
  '✅ Factures & clients illimités',
  '✅ Stripe Connect (encaissement carte)',
  '✅ Connexion IMAP/SMTP',
  '✅ Assistant IA illimité',
  '✅ Studio Visuel IA illimité',
  '✅ Support prioritaire 24h',
  '✅ Sauvegardes incluses',
];

const STARTER_FEATURES = [
  '1 utilisateur',
  '20 factures / mois',
  '50 clients max.',
  'Modules essentiels',
  'Assistant IA basique',
];

export const BillingModule = ({ workspace, setWorkspace }) => {
  const [packages, setPackages] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const pollingRef = useRef(null);

  const currentPlan = (workspace?.plan || 'starter').toLowerCase();
  const activeUntil = workspace?.plan_active_until || null;

  useEffect(() => {
    (async () => {
      try { setPackages(await listPackages()); }
      catch (e) { console.warn('YNX billing: list packages failed', e); }
      finally { setLoading(false); }
    })();
  }, []);

  // ─── Detect return from Stripe via ?billing=success&session_id=... ──
  // Use a ref so the recursive setTimeout always calls the latest version.
  const pollFnRef = useRef(null);
  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= POLL_MAX_ATTEMPTS) {
      setMsg('⏱ Vérification du paiement expirée. Vérifiez votre email pour confirmation.');
      return;
    }
    try {
      const s = await getCheckoutStatus(sessionId);
      if (s.payment_status === 'paid') {
        setMsg('🎉 Paiement confirmé ! Votre plan a été activé.');
        try {
          const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/biz/workspace/${workspace.id}`);
          if (r.ok) {
            const updated = await r.json();
            setWorkspace(updated);
          }
        } catch (err) { console.warn('YNX: workspace refresh after pay failed', err); }
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      if (s.status === 'expired') {
        setMsg('⚠ Session de paiement expirée. Veuillez réessayer.');
        window.history.replaceState({}, '', window.location.pathname);
        return;
      }
      setMsg('⏳ Paiement en cours de traitement…');
      pollingRef.current = setTimeout(
        () => pollFnRef.current && pollFnRef.current(sessionId, attempts + 1),
        POLL_INTERVAL_MS,
      );
    } catch (err) {
      console.warn('YNX billing: status poll error', err);
      setMsg('⚠ Impossible de vérifier le statut. Rechargez la page.');
    }
  }, [workspace, setWorkspace]);

  // Keep the ref pointing to the latest version of the function on every render.
  useEffect(() => { pollFnRef.current = pollPaymentStatus; }, [pollPaymentStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get('billing');
    const sessionId = params.get('session_id');
    if (billing === 'success' && sessionId) {
      setMsg('⏳ Vérification du paiement…');
      pollPaymentStatus(sessionId);
    } else if (billing === 'cancel') {
      setMsg('Paiement annulé. Vous pouvez réessayer quand vous le souhaitez.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    return () => { if (pollingRef.current) clearTimeout(pollingRef.current); };
  }, [pollPaymentStatus]);

  const upgrade = async (pkgId) => {
    setBusy(pkgId); setMsg('');
    try {
      const { url } = await createCheckout(pkgId);
      window.location.href = url;
    } catch (e) {
      setMsg('⚠ Erreur Stripe : ' + e.message);
      setBusy('');
    }
  };

  // Read add-on packages (any package whose id starts with 'addon_')
  const addons = Object.entries(packages).filter(([id]) => id.startsWith('addon_'));
  const soloPkg = packages.solo_30d;
  const businessPkg = packages.business_30d;

  const planBadge = (plan) => {
    const colors = {
      starter:  { bg: 'rgba(148,163,184,0.15)', fg: '#94a3b8' },
      solo:     { bg: 'rgba(94,234,212,0.18)',  fg: '#5eead4' },
      business: { bg: 'rgba(251,191,36,0.18)',  fg: '#fbbf24' },
    };
    const c = colors[plan] || colors.starter;
    return (
      <span style={{
        padding: '4px 12px',
        background: c.bg, color: c.fg,
        fontSize: 11, letterSpacing: 1, borderRadius: 12, fontWeight: 700,
      }}>
        Plan actif : {plan.toUpperCase()}
      </span>
    );
  };

  if (loading) return <div className='biz-loading'>Chargement…</div>;

  return (
    <div className='biz-module'>
      <header className='biz-module-head'>
        <h2>💳 Abonnement YNX</h2>
        {planBadge(currentPlan)}
      </header>

      {msg && <div className='biz-banner success'>{msg}</div>}

      {activeUntil && (
        <div className='biz-info-banner'>
          ⏱ Votre plan <b>{currentPlan.toUpperCase()}</b> est actif jusqu'au{' '}
          <b>{new Date(activeUntil).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</b>.
          Renouvelez avant cette date pour ne pas perdre vos avantages.
        </div>
      )}

      <h3 style={{ marginTop: 8, marginBottom: 12, fontSize: 14, color: 'var(--text-dim)', letterSpacing: 1, textTransform: 'uppercase' }}>
        Plans
      </h3>

      <div className='biz-billing-grid'>
        {/* Starter (gratuit) */}
        <article className={`biz-plan-card ${currentPlan === 'starter' ? 'active' : ''}`}>
          <header>
            <h3>Starter</h3>
            <div className='biz-plan-price'>0 € <span>/ pour toujours</span></div>
          </header>
          <ul className='biz-plan-features'>
            {STARTER_FEATURES.map((f, i) => <li key={`st-${i}`}><Check size={11} /> {f}</li>)}
          </ul>
          <button className='biz-btn-ghost' disabled style={{ width: '100%' }}>
            {currentPlan === 'starter' ? 'Plan actif' : 'Inclus'}
          </button>
        </article>

        {/* Solo (Indépendant 19€) */}
        <article className={`biz-plan-card highlight ${currentPlan === 'solo' ? 'active' : ''}`}>
          <header>
            <h3><Sparkles size={14} /> Indépendant <span className='biz-badge'>POPULAIRE</span></h3>
            <div className='biz-plan-price'>{soloPkg?.amount || 19} € <span>/ 30 jours</span></div>
          </header>
          <ul className='biz-plan-features'>
            {SOLO_FEATURES.map((f, i) => <li key={`so-${i}`}>{f}</li>)}
          </ul>
          <button
            className='biz-btn-primary'
            onClick={() => upgrade('solo_30d')}
            disabled={busy === 'solo_30d' || currentPlan === 'solo'}
            data-testid='upgrade-solo-btn'
            style={{ width: '100%', marginTop: 0 }}
          >
            <CreditCard size={14} />
            {currentPlan === 'solo' ? 'Plan actif' : busy === 'solo_30d' ? 'Redirection…' : `Passer à Indépendant · ${soloPkg?.amount || 19} €`}
          </button>
        </article>

        {/* Business (Entreprise 500€) */}
        <article className={`biz-plan-card premium ${currentPlan === 'business' ? 'active' : ''}`}>
          <header>
            <h3><Crown size={14} /> Entreprise</h3>
            <div className='biz-plan-price'>{businessPkg?.amount || 500} € <span>/ 30 jours</span></div>
          </header>
          <ul className='biz-plan-features'>
            {BUSINESS_FEATURES.map((f, i) => <li key={`bz-${i}`}>{f}</li>)}
          </ul>
          <button
            className='biz-btn-primary'
            onClick={() => upgrade('business_30d')}
            disabled={busy === 'business_30d' || currentPlan === 'business'}
            data-testid='upgrade-business-btn'
            style={{ width: '100%', marginTop: 0, background: '#fbbf24', color: '#0a1426' }}
          >
            <Crown size={14} />
            {currentPlan === 'business' ? 'Plan actif' : busy === 'business_30d' ? 'Redirection…' : `Passer à Entreprise · ${businessPkg?.amount || 500} €`}
          </button>
        </article>
      </div>

      {/* ─── PAID ADD-ONS ─────────────────────────────────── */}
      {addons.length > 0 && (
        <>
          <h3 style={{ marginTop: 36, marginBottom: 6, fontSize: 14, color: 'var(--text-dim)', letterSpacing: 1, textTransform: 'uppercase' }}>
            Options Premium <span style={{ color: '#fbbf24' }}>(+ 200€ / option / mois)</span>
          </h3>
          <p style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 18, lineHeight: 1.6 }}>
            Activez les options dont vous avez besoin. Chacune s'active pour 30 jours et se cumule à votre plan en cours.
          </p>
          <div className='biz-billing-grid' style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
            {addons.map(([pkgId, p]) => (
              <article key={pkgId} className='biz-plan-card'>
                <header>
                  <h3 style={{ fontSize: 16 }}><Plus size={13} style={{ verticalAlign: 'middle' }} /> {p.label.replace(/ — 30 jours$/, '').replace(/^Option : /, '')}</h3>
                  <div className='biz-plan-price' style={{ fontSize: 22 }}>{p.amount} € <span>/ 30 jours</span></div>
                </header>
                <p style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-dim)', minHeight: 70 }}>
                  {p.description}
                </p>
                <button
                  className='biz-btn-primary'
                  onClick={() => upgrade(pkgId)}
                  disabled={busy === pkgId}
                  data-testid={`upgrade-${pkgId}`}
                  style={{ width: '100%', marginTop: 0 }}
                >
                  <Plus size={14} />
                  {busy === pkgId ? 'Redirection…' : `Activer · ${p.amount} €`}
                </button>
              </article>
            ))}
          </div>
        </>
      )}

      <div style={{ marginTop: 18, fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.6 }}>
        🔒 <b>Paiement sécurisé par Stripe</b> — votre carte bancaire n'est jamais stockée sur nos serveurs.
        Tous les plans et options s'activent pour <b>30 jours</b>. Vous pouvez renouveler à tout moment.
        Pas d'engagement, pas de prélèvement automatique.
      </div>
    </div>
  );
};
